// FileContent.js
import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

import styles from './FileContent.module.css'; // Make sure to use your actual styles

// --- Language Imports for Syntax Highlighting ---
import javascript from 'react-syntax-highlighter/dist/esm/languages/prism/javascript';
import jsx from 'react-syntax-highlighter/dist/esm/languages/prism/jsx';
import typescript from 'react-syntax-highlighter/dist/esm/languages/prism/typescript';
import tsx from 'react-syntax-highlighter/dist/esm/languages/prism/tsx';
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python';
import rust from 'react-syntax-highlighter/dist/esm/languages/prism/rust';
import java from 'react-syntax-highlighter/dist/esm/languages/prism/java';
import c from 'react-syntax-highlighter/dist/esm/languages/prism/c';
import cpp from 'react-syntax-highlighter/dist/esm/languages/prism/cpp';
import csharp from 'react-syntax-highlighter/dist/esm/languages/prism/csharp';
import go from 'react-syntax-highlighter/dist/esm/languages/prism/go';
import markup from 'react-syntax-highlighter/dist/esm/languages/prism/markup';
import css from 'react-syntax-highlighter/dist/esm/languages/prism/css';
import scss from 'react-syntax-highlighter/dist/esm/languages/prism/scss';
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
import yaml from 'react-syntax-highlighter/dist/esm/languages/prism/yaml';
import nasm from 'react-syntax-highlighter/dist/esm/languages/prism/nasm';

// --- Register Languages ---
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('jsx', jsx);
SyntaxHighlighter.registerLanguage('typescript', typescript);
SyntaxHighlighter.registerLanguage('tsx', tsx);
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('rust', rust);
SyntaxHighlighter.registerLanguage('java', java);
SyntaxHighlighter.registerLanguage('c', c);
SyntaxHighlighter.registerLanguage('cpp', cpp);
SyntaxHighlighter.registerLanguage('csharp', csharp);
SyntaxHighlighter.registerLanguage('go', go);
SyntaxHighlighter.registerLanguage('markup', markup);
SyntaxHighlighter.registerLanguage('css', css);
SyntaxHighlighter.registerLanguage('scss', scss);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('yaml', yaml);
SyntaxHighlighter.registerLanguage('nasm', nasm);

// --- Helper Functions ---

const getLanguageForExtension = (extension) => {
    const map = {
        js: 'javascript',
        jsx: 'jsx',
        ts: 'typescript',
        tsx: 'tsx',
        py: 'python',
        rs: 'rust',
        java: 'java',
        c: 'c',
        cpp: 'cpp',
        cs: 'csharp',
        go: 'go',
        html: 'markup',
        css: 'css',
        scss: 'scss',
        json: 'json',
        md: 'markdown',
        sh: 'bash',
        yaml: 'yaml',
        yml: 'yaml',
        cu: 'cpp',
        asm: 'nasm',
        ptx: 'nasm',
        sass: 'nasm',
        ino: 'c',
    };
    return map[extension] || 'plaintext';
};

const isImageExtension = (extension) => {
    return ['png', 'jpg', 'jpeg', 'gif', 'svg', 'ico', 'webp'].includes(extension);
};


// --- Main Component ---

export const FileContent = ({ repoName, branch, filePath }) => {
    const [content, setContent] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [fileType, setFileType] = useState('text');

    useEffect(() => {
        if (!repoName || !branch || !filePath) {
            setContent(null);
            return;
        }

        const fetchFileContent = async () => {
            setIsLoading(true);
            setError(null);
            
            const apiUrl = `https://dev.entrosystem.com/api/repos_api/${repoName}/blob/${branch},${filePath}`;

            try {
                const response = await fetch(apiUrl);
                if (!response.ok) {
                    throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
                }

                const contentType = response.headers.get('content-type') || '';
                
                if (contentType.startsWith('image/')) {
                    setFileType('image');
                    // For images, we just need the URL, not the content blob
                    setContent(apiUrl);
                } else if (contentType.startsWith('text/') || contentType.includes('javascript') || contentType.includes('json')) {
                    setFileType('text');
                    const textContent = await response.text();
                    setContent(textContent);
                } else {
                    // Handle binary files or other types we can't preview
                    setFileType('text');
                    const textContent = await response.text();
                    setContent(textContent);
                    // setContent(null); // No content to display
                    // setError(`Cannot preview this binary file type (${contentType}).`);
                }

            } catch (err) {
                setError(err.message);
                setContent(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchFileContent();
    }, [repoName, branch, filePath]);

    const renderContent = () => {
        if (!filePath) {
            return <div className={styles.fileContentPlaceholder}><p>Select a file to view its content.</p></div>;
        }
        if (isLoading) {
            return <div className={styles.loadingSpinner}><div className={styles.spinner}></div><p>Loading file...</p></div>;
        }
        if (error) {
            return <div className={styles.errorContainer}><h3>Error</h3><p>{error}</p></div>;
        }
        if (!content && fileType !== 'binary') {
            return null; // Nothing to show
        }

        const extension = filePath.split('.').pop().toLowerCase();

        if (fileType === 'image') {
            return <img src={content} alt={filePath} className={styles.imagePreview} />;
        }
        
        if (extension === 'md') {
            return <div className={styles.markdownContent}><ReactMarkdown>{content}</ReactMarkdown></div>;
        }

        // Default to syntax-highlighted code for text files
        return (
            <SyntaxHighlighter language={getLanguageForExtension(extension)} style={vscDarkPlus} showLineNumbers>
                {content}
            </SyntaxHighlighter>
        );
    };

    return (
        <div className={styles.fileContentContainer}>
            {filePath && (
                <div className={styles.fileContentHeader}>
                    <h4>{filePath}</h4>
                </div>
            )}
            <div className={styles.fileContentView}>
                {renderContent()}
            </div>
        </div>
    );
};